import React, { useMemo, useRef, useEffect } from "react";
import { connect } from "react-redux";
import * as R from "ramda";
import { withRouter } from "react-router";
import moment from "moment";
import CSSModules from "react-css-modules";
import { actions } from "Portal/PortalHome/model";
import * as PortalSelectors from "redux/modules/portal/selectors";
import { H3, TASK_ACTIONS } from "../constants";
import {
  Div,
  Text1,
  CheckIcon,
  MediumOutlineButton,
  OpenInNewIcon,
  ClockIcon
} from "components/Base";
import StatusCircle from "ui-kit/StatusCircle";
import SubmissionsTable from "./SubmissionsTable";
import css from "./styles.scss";
import { noop } from "utils/General";

const getIcon = type =>
  R.prop(type)({
    document_request: (
      <Div width={26} height={26} ml={1}>
        <span alt="inbox" aria-label="inbox" class="material-icons-outlined">
          inbox
        </span>
      </Div>
    ),
    people: (
      <Div width={25} height={25} ml={1}>
        <img
          alt="portal-icon-people"
          aria-label="portal-icon-people"
          src="https://d2dks4tzxs6xee.cloudfront.net/img/portal-icon-people.png"
          width="25"
          height="25"
        />
      </Div>
    ),
    task: (
      <Div width={25} height={25} ml={1}>
        <span
          alt="task_alt"
          aria-label="task_alt"
          class="material-icons-outlined"
        >
          task_alt
        </span>
      </Div>
    ),
    assigned_task: (
      <Div width={25} height={25} ml={1}>
        <span
          alt="task_alt"
          aria-label="task_alt"
          class="material-icons-outlined"
        >
          task_alt
        </span>
      </Div>
    ),
    form: (
      <Div width={26} height={26} ml={1}>
        <span alt="web" aria-label="web" class="material-icons-outlined">
          web
        </span>
      </Div>
    ),
    followup_form: (
      <Div width={26} height={26} ml={1}>
        <span alt="web" aria-label="web" class="material-icons-outlined">
          web
        </span>
      </Div>
    ),
    invoice: (
      <Div width={30} height={30} ml={1}>
        <img
          alt="portal-icon-invoice"
          aria-label="portal-icon-invoice"
          src="https://d2dks4tzxs6xee.cloudfront.net/img/portal-icon-invoice.png"
          width="30"
          height="30"
        />
      </Div>
    )
  });
const statusCircleMap = {
  denied: { status: "rejected", size: 30 },
  pending: { status: "pending", size: 30 },
  complete: { status: "approved", size: 30 },
  approved: { status: "approved", size: 30 },
  default: { status: "default", size: 30 }
};

const decorate = R.compose(
  withRouter,
  connect(
    state => ({
      portal: PortalSelectors.portal(state)
    }),
    {
      executeAction: actions.executeAction
    }
  ),
  CSSModules(css)
);

export const Task = ({ type, data, executeAction, portal, ...otherProps }) => {
  const {
    status,
    name,
    description = null,
    is_required,
    count,
    open_label,
    due_label,
    is_past_due,
    meta = {},
    due_raw
  } = data;

  const metaUrl = R.path(["meta", "url"], data);

  const submissions = R.propOr([], "submissions", data);
  const timezone = R.pathOr("America/Los_Angeles", ["event", "timezone"])(
    portal
  );
  const formattedDueRaw = moment(due_raw)
    .tz(timezone)
    .format("[Due] MMM DD, YYYY [at] h:mm A");
  const timezoneFormatted = moment()
    .tz(timezone)
    .format("z");
  const icon = getIcon(type);
  const getIncludesAgo = () => {
    if (!due_label || !R.is(String, due_label)) return null;
    return due_label.includes("ago") ? "Closed" : "Open";
  };
  const formattedOpenLabel = open_label || getIncludesAgo();
  const showRightContainer =
    formattedOpenLabel ||
    due_label ||
    R.propEq("completionMethod", "manual")(meta) ||
    (R.propEq("completionMethod", "click")(meta) &&
      metaUrl &&
      status === "complete");
  const isOnClickAndCompleted =
    R.propEq("completionMethod", "click")(meta) &&
    metaUrl &&
    status === "complete";
  const hasDueDateLabel = formattedOpenLabel || due_label;
  const isManual = R.propEq("completionMethod", "manual")(meta);
  const dueDateInfo = useMemo(
    () => (
      <Div
        fs={2}
        color="black"
        display="row.flex-end.center"
        style={{
          flexShrink: 0
        }}
      >
        {[formattedOpenLabel, due_label].filter(v => v).join(" · ")}
        {due_label && (
          <Div display="row.flex-start.center" ml={2}>
            <ClockIcon
              color="black"
              tooltip={`${formattedDueRaw} ${timezoneFormatted}`}
              size={14}
            />
          </Div>
        )}
      </Div>
    ),
    [formattedOpenLabel, due_label, formattedDueRaw, timezoneFormatted]
  );
  const toggleTaskButton = status => (
    <MediumOutlineButton
      mt={2}
      LeftIcon={CheckIcon}
      onClick={e => {
        e.stopPropagation();
        executeAction({ type: "toggle_task", data });
      }}
    >
      {status === "complete" ? "Completed" : "Mark as Complete"}
    </MediumOutlineButton>
  );
  const statusCircle =
    statusCircleMap[status] ||
    (is_past_due ? { status: "not-submitted", size: 30 } : null);
  const hasSubmissions = !R.isEmpty(submissions) && !R.isNil(submissions);

  let descriptionRef = useRef();

  // force to open all the links in description to a new tab
  // https://lenndapp.atlassian.net/browse/SB-3174
  //
  useEffect(() => {
    const { current = null } = descriptionRef;
    if (!R.isNil(current)) {
      const aRefs = current.querySelectorAll("a[href]");
      if (!R.isEmpty(aRefs)) {
        R.forEach(aR => {
          aR.setAttribute("target", "_blank");
        }, aRefs);
      }
    }
  }, []);

  return (
    <Div
      className={css.portalTaskDescriptionContainer}
      display="column"
      bg="white"
      px={3}
      py={3}
      bra={3}
      mb={3}
      shadow={{
        default: 0,
        hover: 2
      }}
      style={{
        cursor: "pointer",
        border: is_past_due ? "1px solid rgb(245, 159, 0)" : undefined
      }}
      onClick={
        hasSubmissions ? undefined : () => executeAction({ type: type, data })
      }
      {...otherProps}
    >
      <Div
        display="row.space-between.center"
        className={css.taskContent}
        onClick={
          hasSubmissions ? () => executeAction({ type: type, data }) : undefined
        }
      >
        <Div
          className={
            showRightContainer
              ? css.contentContainerWithRightContent
              : css.contentContainerWithoutRightContent
          }
          display="row.flex-start.center"
        >
          <StatusCircle
            status={R.prop("status", statusCircle)}
            size={R.prop("size", statusCircle)}
          />

          {icon}

          <Div display="column" flex={1} ml={icon ? 2 : 3} mr={2}>
            <Div
              fs={5}
              fw={3}
              color="black"
              className={`${description ? css.taskName : ""}`}
            >
              <H3
                style={{
                  margin: "inherit",
                  wordBreak: "break-word",
                  fontSize: "22px",
                  width: "fit-content"
                }}
                onClick={
                  metaUrl
                    ? e => {
                        e.stopPropagation();
                        executeAction({
                          type: TASK_ACTIONS.OPEN_FILE,
                          url: metaUrl
                        });
                      }
                    : noop
                }
              >
                {name}
                {is_required && <span style={{ color: "#c92a2a" }}> *</span>}
                {metaUrl && <OpenInNewIcon ml={2} size={17} color="neutral5" />}
              </H3>
            </Div>
            {count ? (
              <Div bg="neutral2" pill pr={2} pl={2}>
                <Text1 bold>{count}</Text1>
              </Div>
            ) : null}
            {description && description.length ? (
              <div
                className={css.taskDescription}
                ref={descriptionRef}
                dangerouslySetInnerHTML={{ __html: description }}
              ></div>
            ) : null}
          </Div>
        </Div>

        {showRightContainer && (
          <Div className={css.buttonContainer}>
            {isOnClickAndCompleted || isManual ? (
              <Div display="column">
                {hasDueDateLabel && dueDateInfo}
                {toggleTaskButton(status)}
              </Div>
            ) : (
              hasDueDateLabel && dueDateInfo
            )}
          </Div>
        )}
      </Div>

      {hasSubmissions ? (
        <SubmissionsTable
          id={R.prop("id", data)}
          slug={R.prop("form_slug", data)}
          baseModuleId={R.prop("base_module_id", data)}
          isOpen={R.prop("open_label", data) !== "Closed"}
          count={R.prop("count", data)}
          countOfDrafts={R.prop("count_of_drafts", data)}
          countOfSubmitted={R.prop("count_of_submitted", data)}
          blockSubmit={
            R.propEq("single_submission", true, data) &&
            R.propOr(0, "count", data) >= 1
          }
        />
      ) : null}
    </Div>
  );
};

export default decorate(Task);
